import type { OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { PreviousPackage } from '../packageOptions/previousDealOptions';
import type { OptionState, PackageOptionId } from '../packageOptions/types';

import { INCLUSION_TYPE_OPTIONS } from './fields';
import { getPackageRooms } from './inclusionsModel';
import type { InclusionFormValues } from './types';

export type PreviousInclusions = {
  drafts: Array<InclusionFormValues>;
  notCopied: number;
  otherCurrencyValue: number;
  oldAssetNames: Array<string>;
};

// The tab's PRV is AUD; Salesforce values an inclusion in its own currency.
const PRV_CURRENCY = 'AUD';

type InclusionCell = {
  inclusion: OpportunityRelatedInclusion;
  roomId: PackageOptionId;
  nights: number;
};

// Salesforce repeats an inclusion on every Deal Option it applies to, so identical rows are one inclusion.
function inclusionKey(inclusion: OpportunityRelatedInclusion, name: string): string {
  return [
    inclusion.asset__c ?? '',
    name.toLowerCase(),
    inclusion.type__c ?? '',
    inclusion.currencyisocode ?? '',
    Number(inclusion.rrp__c ?? 0),
  ].join('::');
}

function descriptionOf(inclusion: OpportunityRelatedInclusion): string {
  return String(inclusion.inclusion_description__c ?? '').trim();
}

// Salesforce sometimes leaves the fine print empty on some of the rows it repeats, so a group only splits when
// its rows carry different non-empty fine print; a blank one then stays apart too.
function splitByDescription(cells: Array<InclusionCell>): Array<Array<InclusionCell>> {
  const descriptions = new Set(cells.map(({ inclusion }) => descriptionOf(inclusion)).filter(Boolean));
  if (descriptions.size <= 1) {
    return [cells];
  }
  const cellsByDescription = new Map<string, Array<InclusionCell>>();
  for (const cell of cells) {
    const description = descriptionOf(cell.inclusion);
    cellsByDescription.set(description, [...(cellsByDescription.get(description) ?? []), cell]);
  }
  return [...cellsByDescription.values()];
}

function valueOf(inclusion: OpportunityRelatedInclusion): number | undefined {
  const totalValue = Number(inclusion.total_value__c);
  return totalValue > 0 ? totalValue : undefined;
}

// PRV is the value on the lead package: Salesforce's Total Value on the shortest stay (RRP × nights when Daily).
function toDraft(cells: Array<InclusionCell>, option: OptionState, currency: string): InclusionFormValues {
  const lead = cells.reduce((shortest, cell) => (cell.nights < shortest.nights ? cell : shortest));
  const { inclusion } = lead;
  const name = String(inclusion.name).trim();
  const description = [lead, ...cells].map((cell) => descriptionOf(cell.inclusion)).find(Boolean);
  // "All" on the Inclusions tab means every row of every category, so it is only kept when every row has it.
  const optionRoomIds = getPackageRooms(option).map((room) => room.id);
  const roomIds = optionRoomIds.filter((id) => cells.some((cell) => cell.roomId === id));
  const los = option.lengthsOfStay.filter((nights) => cells.some((cell) => cell.nights === nights));

  return {
    pricingOptionIndex: 0,
    name,
    description: description ?? '',
    type: INCLUSION_TYPE_OPTIONS.find((type) => type === inclusion.type__c),
    validFor: [],
    // RRP is in the inclusion's own currency, so it only carries over when that matches this session's.
    rrp: inclusion.currencyisocode === currency && Number(inclusion.rrp__c) > 0 ? Number(inclusion.rrp__c) : undefined,
    prv: inclusion.currencyisocode === PRV_CURRENCY ? valueOf(inclusion) : undefined,
    // Unlinked ones get an asset by name when copied in - see docs/business-rules.md#inclusion-assets.
    assetLink: inclusion.asset__c
      ? { assetId: inclusion.asset__c, assetName: inclusion.asset__name ?? inclusion.asset?.name ?? name }
      : undefined,
    applyTo: {
      roomTypes: roomIds.length === optionRoomIds.length ? 'all' : roomIds,
      occupancies: 'all',
      los: los.length === option.lengthsOfStay.length ? 'all' : los,
    },
  };
}

// Applies-to is rooms × LOS, so rooms covered on different stay lengths become one draft per LOS set.
function toDrafts(cells: Array<InclusionCell>, option: OptionState, currency: string): Array<InclusionFormValues> {
  const nightsByRoom = new Map<PackageOptionId, Set<number>>();
  for (const { roomId, nights } of cells) {
    nightsByRoom.set(roomId, (nightsByRoom.get(roomId) ?? new Set()).add(nights));
  }
  const roomsByLosSet = new Map<string, Set<PackageOptionId>>();
  for (const [roomId, nights] of nightsByRoom) {
    const losSet = [...nights].sort((a, b) => a - b).join(',');
    roomsByLosSet.set(losSet, (roomsByLosSet.get(losSet) ?? new Set()).add(roomId));
  }
  return [...roomsByLosSet.values()].map((roomIds) =>
    toDraft(
      cells.filter((cell) => roomIds.has(cell.roomId)),
      option,
      currency,
    ),
  );
}

// Matched on suffix too: one room can have several Upgrade rows.
function copiedCellsByDealId(packages: Array<PreviousPackage>, option: OptionState) {
  const cells = new Map<string, Pick<InclusionCell, 'roomId' | 'nights'>>();
  for (const { asset, categoryIndex, suffix, dealIdByLos } of packages) {
    const row = option.roomsByCategory[categoryIndex]?.find(
      (room) => room.assetId === asset.id && (room.suffix ?? '') === (suffix ?? ''),
    );
    if (row) {
      Object.entries(dealIdByLos).forEach(([nights, dealId]) =>
        cells.set(dealId, { roomId: row.id, nights: Number(nights) }),
      );
    }
  }
  return cells;
}

// Inclusions of a Deal Option the copy left out are dropped, since it has no row to attach to.
export function reducePreviousInclusions(
  inclusions: Array<OpportunityRelatedInclusion> = [],
  packages: Array<PreviousPackage>,
  option: OptionState,
  currency: string,
  // See docs/business-rules.md#recent-assets. Undefined copies every asset.
  recentAssetSfids?: Set<string>,
): PreviousInclusions {
  const cellByDealId = copiedCellsByDealId(packages, option);
  const cellsByKey = new Map<string, Array<InclusionCell>>();
  const oldAssetNames = new Set<string>();

  for (const inclusion of inclusions) {
    const name = inclusion.name?.trim();
    if (!name) {
      continue;
    }
    const cell = inclusion.deal_offer__c ? cellByDealId.get(inclusion.deal_offer__c) : undefined;
    if (cell && recentAssetSfids && inclusion.asset__c && !recentAssetSfids.has(inclusion.asset__c)) {
      oldAssetNames.add(name);
      continue;
    }
    const key = inclusionKey(inclusion, name);
    const cells = cellsByKey.get(key) ?? [];
    cellsByKey.set(key, cells);

    if (cell) {
      cells.push({ inclusion, ...cell });
    }
  }

  const copiedCells = [...cellsByKey.values()].filter((cells) => cells.length > 0);
  return {
    drafts: copiedCells.flatMap(splitByDescription).flatMap((cells) => toDrafts(cells, option, currency)),
    notCopied: cellsByKey.size - copiedCells.length,
    otherCurrencyValue: copiedCells.filter(
      ([{ inclusion }]) => inclusion.currencyisocode !== PRV_CURRENCY && valueOf(inclusion) !== undefined,
    ).length,
    oldAssetNames: [...oldAssetNames],
  };
}
